import { FC, useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import {
  Box, Grid, Table, TableBody, TableHead, TableRow, TableCell, tableCellClasses, Chip, Button, CircularProgress,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchUnits } from "../../store/unitsStore/unitsThunk";
import { useInfiniteReport } from "../../hooks/useInfiniteReport";
import ReusableReportHeader from "../Reports/ReusableReportHeader";
import LocationReportFilter from "./pickers/LocationReportFilter";
import { useReportLocationId } from "./pickers/useReportLocationId";
import { FilterSelect } from "./InvFilters";
import Filters from "../Filters/Filters";
import { movementReasonLabel } from "../../utils/inventoryConstants";

// Stock Out report — one row per outward movement (qtyDelta < 0), server-paginated + server-side filters.

// Reasons that can take stock OUT (direction=out already limits to qtyDelta < 0;
// this dropdown just narrows within that).
const OUT_REASONS = ["sale", "transfer_out", "wastage", "production_consume", "adjustment", "grn_reversed", "transfer_reversed"];

const REASON_COLOR: Record<string, string> = {
  sale: "#ef4444", transfer_out: "#488dff", wastage: "#b08900",
  production_consume: "#8b5cf6", adjustment: "#3b82f6", grn_reversed: "#c33b3b", transfer_reversed: "#c33b3b",
};

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#eaecf0", color: "#000", border: "0.75px solid #d9d4d4",
    padding: 5, fontSize: "9pt", paddingLeft: "12px", fontWeight: 600, whiteSpace: "nowrap",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "9pt", fontWeight: "normal", color: "#292d35",
    padding: 5, border: "0.75px solid #d9d4d4", paddingLeft: "12px", verticalAlign: "top",
  },
}));

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");
const num = (n: any) => Number(n ?? 0);
const esc = (s: any) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

const ALL_COLS = ["Date", "Item", "Item Code", "Reason", "Location", "Batch", "Qty Out", "Unit", "Balance After", "Ref", "Note"];

const StockOutReport: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const unitsById = useAppSelector((state) => state.units.byId);
  const unitsList = useAppSelector((state) => state.units.units);

  // entryUnit is stored as a MeasuringUnit id — resolve to a readable code/name.
  const unitText = (u?: string) => (u ? unitsById?.[u]?.code || unitsById?.[u]?.name || u : "");

  const [locationId, setLocationId] = useReportLocationId({ startOn: "all" }); // opens on All Locations
  const [reasonFilter, setReasonFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // Bumped on reset to remount the shared date-range dropdown back to "Select Date".
  const [filtersKey, setFiltersKey] = useState(0);

  useEffect(() => {
    if (!unitsList || unitsList.length === 0) dispatch(fetchUnits());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side, scroll-paginated data (50 records/page) — outward movements only.
  const { rows, total, loading, loadingMore, hasMore, loadMore, collectAll } = useInfiniteReport(
    "/api/v1/erp/stock/movements",
    { direction: "out", locationId, reason: reasonFilter, from: fromDate, to: toDate }
  );

  // Shared Filters dropdown emits "start=YYYY-MM-DD&end=YYYY-MM-DD" (empty for "Select Date").
  const handleDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    unstable_batchedUpdates(() => {
      setFromDate(params.get("start") || "");
      setToDate(params.get("end") || "");
    });
  };

  const resetFilters = () => {
    setLocationId(""); setReasonFilter(""); setFromDate(""); setToDate("");
    setFiltersKey((k) => k + 1);
  };

  const onScroll = (e: any) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 300 && hasMore && !loadingMore && !loading) loadMore();
  };

  const rangeText = fromDate || toDate ? `${fromDate ? fmtDate(fromDate) : "…"} – ${toDate ? fmtDate(toDate) : "…"}` : "All dates";

  // Ref column: the source document number when we can resolve it (transfers carry
  // their number via the read-time `source` enrichment), else the raw refType.
  const refText = (m: any) => m.source?.transferNumber || m.refType || "-";

  // -------- export (pages through the FULL filtered set, not just what's loaded) --------
  const flatRows = (data: any[]): string[][] =>
    data.map((m: any): string[] => [
      fmtDate(m.createdAt), m.skuId?.name || "", m.skuId?.itemCode || "", movementReasonLabel(m.reason),
      m.locationId?.name || "", m.batchId?.batchNumber || "",
      String(Math.abs(num(m.qtyDelta))), m.skuId?.baseUnitName || "",
      String(num(m.balanceAfter)), refText(m) === "-" ? "" : refText(m), m.note || "",
    ]);

  const buildTableHtml = (data: any[]) => `
    <table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse;font-family:Arial;font-size:11px">
      <thead><tr style="background:#eaecf0">${ALL_COLS.map((c) => `<th align="left">${esc(c)}</th>`).join("")}</tr></thead>
      <tbody>${flatRows(data).map((r) => `<tr>${r.map((v) => `<td>${esc(v)}</td>`).join("")}</tr>`).join("")}</tbody>
    </table>`;

  const handleDownloadExcel = async () => {
    const all = await collectAll();
    const html = `<html><head><meta charset="utf-8" /></head><body>${buildTableHtml(all)}</body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Stock_Out_Report_${new Date().toISOString().split("T")[0]}.xls`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async () => {
    const all = await collectAll();
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Stock Out Report</title>
      <style>@page{size:A4 landscape;margin:10mm}
      body{font-family:Arial;padding:0} h3{margin:0 0 4px} .sub{color:#666;font-size:12px;margin-bottom:12px}
      table{border-collapse:collapse;width:100%;font-size:10px} th,td{border:0.75px solid #d9d4d4;padding:4px;text-align:left}
      thead tr{background:#eaecf0}</style></head>
      <body><h3>Stock Out Report</h3><div class="sub">${esc(rangeText)} · ${all.length} outward movements</div>
      ${buildTableHtml(all)}</body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:absolute;width:0;height:0;border:0;left:-9999px;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) return false;
    doc.open(); doc.write(html); doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus(); iframe.contentWindow?.print();
        setTimeout(() => document.body.removeChild(iframe), 500);
      }, 300);
    };
    return "print";
  };

  // Dashboard report-card's download button opens this page with ?download=excel|pdf —
  // trigger that export once and clean the URL.
  useEffect(() => {
    const dl = new URLSearchParams(window.location.search).get("download");
    if (!dl) return;
    window.history.replaceState({}, "", window.location.pathname);
    const t = setTimeout(() => { dl === "pdf" ? handleDownloadPDF() : handleDownloadExcel(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ width: "100%", pr: 1 }}>
      <Box sx={{ width: "100%", overflow: "hidden", mb: 2 }} className={"c-card2"}>
        <ReusableReportHeader
          reportType="stock_out"
          title="Stock Out Report"
          subtitle={`${rangeText} · ${total} outward movements`}
          recordCount={total}
          isLoading={loading}
          backTo="/inventory-dashboard"
          showPrint={false}
          showShare={false}
          onDownloadPDF={handleDownloadPDF}
          onDownloadExcel={handleDownloadExcel}
        />

        {/* Filters strip */}
        <Grid container spacing={1.5} alignItems="center" p={2} style={{ borderBottom: ".5px solid #9da7b7" }}>
          <Grid item>
            <LocationReportFilter borderless width={200} value={locationId} onChange={(v: string) => setLocationId(v)} label="Location" activeOnly={false} />
          </Grid>
          <Grid item>
            <FilterSelect
              value={reasonFilter}
              onChange={(v: string) => setReasonFilter(v)}
              placeholder="Reason"
              width={180}
              options={OUT_REASONS.map((r) => ({ label: movementReasonLabel(r), value: r }))}
            />
          </Grid>
          <Grid item>
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <span style={{ position: "absolute", top: -8, left: 20, padding: "0 5px", background: "#fff", fontSize: 12, color: "#6b7280", lineHeight: 1, zIndex: 1, pointerEvents: "none" }}>Date</span>
              <Filters key={filtersKey} type="inventory-stock-out" dateOnly hidePartySearch hideFilter hideReset filterHandler={handleDateFilter} />
            </div>
          </Grid>
          <Grid item>
            <Button size="small" color="inherit" startIcon={<RestartAltIcon />} onClick={resetFilters} sx={{ color: "#7a8290" }}>
              Reset
            </Button>
          </Grid>
        </Grid>

        {/* Report table — one row per outward movement; scroll loads more */}
        <div onScroll={onScroll} style={{ maxHeight: "62vh", overflow: "auto" }}>
          <Table stickyHeader sx={{ minWidth: 1300 }} size="small" aria-label="stock out report">
            <TableHead>
              <TableRow>
                {ALL_COLS.map((c) => <StyledTableCell key={c}>{c}</StyledTableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? rows.map((m: any) => (
                <TableRow key={m._id}>
                  <StyledTableCell>{fmtDate(m.createdAt)}</StyledTableCell>
                  <StyledTableCell>
                    <span style={{ color: "#488dff", cursor: "pointer" }} onClick={() => navigate(`/inventory/view/${m.skuId?._id || m.skuId}`)}>
                      {m.skuId?.name || "-"}
                    </span>
                  </StyledTableCell>
                  <StyledTableCell>{m.skuId?.itemCode || "-"}</StyledTableCell>
                  <StyledTableCell>
                    <Chip size="small" label={movementReasonLabel(m.reason)} sx={{ color: REASON_COLOR[m.reason] || "#555" }} />
                  </StyledTableCell>
                  <StyledTableCell>{m.locationId?.name || "-"}</StyledTableCell>
                  <StyledTableCell>{m.batchId?.batchNumber || "-"}</StyledTableCell>
                  <StyledTableCell>
                    <span style={{ color: "#c33b3b", fontWeight: 600 }}>{Math.abs(num(m.qtyDelta))}</span>
                    {m.entryUnit && m.entryQty != null ? (
                      <div style={{ color: "#9da7b7", fontSize: "8pt", marginTop: 2 }}>({Math.abs(num(m.entryQty))} {unitText(m.entryUnit)})</div>
                    ) : null}
                  </StyledTableCell>
                  <StyledTableCell>{m.skuId?.baseUnitName || "-"}</StyledTableCell>
                  <StyledTableCell>{num(m.balanceAfter)}</StyledTableCell>
                  <StyledTableCell>{refText(m)}</StyledTableCell>
                  <StyledTableCell>{m.note || "-"}</StyledTableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <StyledTableCell colSpan={ALL_COLS.length} style={{ textAlign: "center", color: "#9da7b7", padding: 24 }}>
                    {loading ? "Loading…" : "No outward stock movements match the selected filters."}
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {loadingMore ? (
            <div style={{ textAlign: "center", padding: 12 }}><CircularProgress size={20} /></div>
          ) : (!hasMore && rows.length > 0) ? (
            <div style={{ textAlign: "center", padding: 10, color: "#c7ccd4", fontSize: 12 }}>All {total} movements loaded</div>
          ) : null}
        </div>
      </Box>
    </Box>
  );
};

export default StockOutReport;
